import { Flex, ContextMenuSeparator, Spinner } from '@cad-arc';
import { MenuLoadingError } from '../common/components/menu-loading-error';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { AssignAsBackupToSubmenu } from './assign-as-backup-to-submenu';
import { AssignBackupItem } from './assign-backup-item';
import { EditTemporaryUnitItem } from './edit-temporary-unit-item';
import { EditUnitItem } from './edit-unit-item';
import { InitiateSubmenu } from './initiate-submenu';
import { OffDutyItem } from './off-duty-item';
import { ReleaseBackupItem } from './release-backup-item';
import { ReleaseItem } from './release-item';
import { UnitLocationItem } from './unit-location-item';
import { UnitStatusSubmenu } from './unit-status-submenu';
import { UnitTimerSubmenu } from './unit-timer-submenu';
import { ViewOnMapItem } from './view-on-map-item';
import { UnitHistoryItem } from './unit-history-item';
import { useUnitContextMenuQuery } from './use-unit-context-menu-query';
import { EndEmergencyItem } from './end-emergency-item';

export function UnitContextMenu({
    unitId,
    isContextMenuOpenFromAMap = false,
}: {
    unitId: number;
    isContextMenuOpenFromAMap?: boolean;
}): React.ReactElement {
    const { data: unitContextMenuView, isInitialLoading, isError } = useUnitContextMenuQuery({
        unitId,
    });

    if (isInitialLoading) {
        return (
            <Flex justifyContent="center" alignItems="center" w="100%" h="100%">
                <Spinner />
            </Flex>
        );
    }

    if (isError) {
        return <MenuLoadingError />;
    }

    if (!unitContextMenuView) {
        return <div />;
    }

    const { clickParams } = unitContextMenuView;

    return (
        <ErrorBoundary
            name="UNIT_CONTEXT_MENU"
            messageVerbNoun="opening the unit menu"
            asToast={true}
        >
            <UnitLocationItem
                contextMenuItemView={unitContextMenuView.unitLocationContextMenuItemView}
                clickParams={clickParams}
            />
            <InitiateSubmenu
                initiateSubmenuView={unitContextMenuView.initiateSubmenuView}
                clickParams={clickParams}
            />
            {unitContextMenuView.showSeparator1 && (
                <ContextMenuSeparator data-testid="unit-context-menu-separator-1" />
            )}
            <UnitStatusSubmenu
                unitStatusSubmenuView={unitContextMenuView.unitStatusSubmenuView}
                clickParams={clickParams}
            />
            <UnitTimerSubmenu
                unitTimerSubmenuView={unitContextMenuView.unitTimerSubmenuView}
                clickParams={clickParams}
            />
            <AssignBackupItem
                contextMenuItemView={unitContextMenuView.assignBackupContextMenuItemView}
                clickParams={clickParams}
            />
            <AssignAsBackupToSubmenu
                assignAsBackupToSubmenuView={unitContextMenuView.assignAsBackupToSubmenuView}
                clickParams={clickParams}
            />
            <ReleaseBackupItem
                contextMenuItemView={unitContextMenuView.releaseBackupContextMenuItemView}
                clickParams={clickParams}
            />
            <ReleaseItem
                contextMenuItemView={unitContextMenuView.releaseContextMenuItemView}
                clickParams={clickParams}
            />
            {!isContextMenuOpenFromAMap && (
                <ViewOnMapItem
                    contextMenuItemView={unitContextMenuView.viewOnMapContextMenuItemView}
                    clickParams={clickParams}
                />
            )}
            {unitContextMenuView.showSeparator2 && (
                <ContextMenuSeparator data-testid="unit-context-menu-separator-2" />
            )}
            <UnitHistoryItem
                contextMenuItemView={unitContextMenuView.unitHistoryContextMenuItemView}
                clickParams={clickParams}
            />
            <EditUnitItem
                contextMenuItemView={unitContextMenuView.editUnitContextMenuItemView}
                clickParams={clickParams}
            />
            <EditTemporaryUnitItem
                contextMenuItemView={unitContextMenuView.editTemporaryUnitContextMenuItemView}
                clickParams={clickParams}
            />
            <OffDutyItem
                contextMenuItemView={unitContextMenuView.offDutyContextMenuItemView}
                clickParams={clickParams}
            />
            <EndEmergencyItem
                contextMenuItemView={unitContextMenuView.endEmergencyContextMenuItemView}
                clickParams={clickParams}
            />
        </ErrorBoundary>
    );
}
